import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Menu, Spin } from 'antd';
const { Header, Content } = Layout;
import classNames from 'classnames';
import { DatabaseOutlined, ClockCircleOutlined, ReadOutlined, CarryOutOutlined, TagsOutlined,
    FormOutlined, SnippetsOutlined, AppstoreAddOutlined, BookOutlined, HomeOutlined, CloudServerOutlined,
    BlockOutlined, UserOutlined, LineChartOutlined,
    CrownOutlined, BarChartOutlined, RobotOutlined,
    TrophyOutlined,
    SafetyOutlined,
    ImportOutlined,
    AppstoreOutlined, PartitionOutlined, 
    CaretDownFilled, 
    LeftOutlined,
    RightOutlined } from '@ant-design/icons';
import { Link, routeHistory } from '@nackle/biw-web-router';
import { Routes } from '../../../routes';
import { authService } from '../../../auth/authService';
import { CompanyContext } from '../../../providers/companyProvider';
import { useFeatureFlags } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags';
import './styles.less';

// Hover-scroll rate for the nav overflow arrows, in px per second. Slow enough that
// labels stay readable as they pass under the pointer.
const NAV_SCROLL_SPEED = 120;

/**
 * ⚠️ SHARED NAV CONFIG — TECH DEBT (implement eventually)
 *
 * The top-level navigation structure (groups → child pages, with their icons,
 * labels, routes and feature flags) is currently duplicated across THREE places:
 *   1. This menu (AppView.js)
 *   2. The "middleman" landing pages: LearningContentNav, ContentTaxonomyNav,
 *      GamificationNav (/incentives), ReportingNavPage (/reporting)
 *   3. The home page cards (HomeView.js)
 *
 * These must be kept in sync BY HAND today, which has already caused drift
 * (e.g. gating mismatches between the menu and the home cards). The intended
 * fix is to extract a single source of truth (one config array describing each
 * group and its children) and render the menu, landing pages, and home cards
 * from it. Until then: any change here must be mirrored in (2) and (3).
 */
const AppView = ( props ) => {
    const { loading: companyLoading } = useContext( CompanyContext );
    const [ pathname, setPathname ] = useState( window.location.pathname );
    const [
        showCurricula, showCategories, showCertificates, showDataDump,
        showResources, showNotifications, showPageSetup, showObjectives, showAIReporting,
        showILT, showStatichouseReporting, showLeaderboards, showBadges,
    ] = useFeatureFlags( [
            flags.CURRICULA, flags.CATEGORIES, flags.CERTIFICATES, flags.DATA_EXPORT,
            flags.RESOURCES, flags.NOTIFICATIONS, flags.PAGE_SETUP, flags.OBJECTIVES, flags.AI_REPORTING_ENABLED,
            flags.ILT_ENABLED, flags.STATIC_REPORTING_ENABLED, flags.LEADERBOARDS, flags.BADGES ] );

    // Keep the active menu item in sync with the current URL (rather than the last
    // click) so the highlight survives navigation and reloads.
    useEffect( () => {
        const handleRouteChanged = () => setPathname( window.location.pathname );
        routeHistory.addEventListener( 'changed', handleRouteChanged );
        routeHistory.refresh();
        return () => routeHistory.removeEventListener( 'changed', handleRouteChanged );
    }, [] );

    // Map each menu key to the route it represents. antd marks a parent submenu as
    // selected automatically when one of its children is selected, so listing the
    // children (plus the parent landing pages) is enough to light up both levels.
    // Parent/landing entries are matched last via longest-prefix, so e.g.
    // /reporting/static selects "static-reporting" rather than the "reporting" parent.
    const selectedKeys = useMemo( () => {
        const menuRoutes = [
            { key: 'learning-objects', route: Routes.Objects },
            { key: 'courses', route: Routes.Courses },
            { key: 'curriculums', route: Routes.Curriculums },
            { key: 'resources', route: Routes.ResourcesPage },
            { key: 'ilt', route: Routes.ILTPage },
            { key: 'categories', route: Routes.ListCategoriesView },
            { key: 'tags', route: Routes.TagsPage },
            { key: 'objectives', route: Routes.ObjectivesPage },
            { key: 'leaderboards', route: Routes.ViewLeaderboardsPage },
            { key: 'badges', route: Routes.ViewBadgesPage },
            { key: 'certificates', route: Routes.ListCertificates },
            { key: 'data-export', route: Routes.DataExportPage },
            { key: 'static-reporting', route: Routes.StaticReporting },
            { key: 'ai-reporting', route: Routes.AIReport },
            { key: 'stats', route: Routes.Stats },
            { key: 'warehouse-imports', route: Routes.WarehouseImports },
            { key: 'notifications', route: Routes.NotificationsPage },
            { key: 'pagesetup', route: Routes.ComponentsList },
            { key: 'learning-content', route: Routes.LearningContentNav },
            { key: 'content-taxonomy', route: Routes.ContentTaxonomyNav },
            { key: 'gamification', route: Routes.GamificationNav },
            { key: 'reporting', route: Routes.Reporting },
        ];

        let bestKey;
        let bestLen = -1;
        for ( const { key, route } of menuRoutes ) {
            const base = route.buildPath().split( '?' )[ 0 ];
            const isMatch = pathname === base || pathname.startsWith( `${ base }/` );
            if ( isMatch && base.length > bestLen ) {
                bestKey = key;
                bestLen = base.length;
            }
        }
        return bestKey ? [ bestKey ] : [];
    }, [ pathname ] );

    // The nav menu scrolls horizontally (.nav-menu is overflow-x: auto), so track
    // which side currently has items scrolled out of view and show only the matching
    // arrow. This has to be measured in JS — there is no CSS expression for "this
    // element is scrolled away from its start/end".
    const holderRef = useRef( null );
    const [ overflow, setOverflow ] = useState( { left: false, right: false } );

    useEffect( () => {
        const menu = holderRef.current?.querySelector( '.nav-menu' );
        if ( !menu ) { return undefined; }

        // 1px of slack: fractional layout widths can leave scrollLeft a hair short of
        // its true maximum, which would otherwise pin the right arrow on permanently.
        const update = () => setOverflow( {
            left: menu.scrollLeft > 1,
            right: menu.scrollLeft + menu.clientWidth < menu.scrollWidth - 1
        } );

        update();
        menu.addEventListener( 'scroll', update, { passive: true } );

        // catches the window/container resizing the menu in or out of overflow
        const resizeObserver = new ResizeObserver( update );
        resizeObserver.observe( menu );

        return () => {
            menu.removeEventListener( 'scroll', update );
            resizeObserver.disconnect();
        };
    }, [ companyLoading ] );

    // Hovering an arrow scrolls the menu that way until the pointer leaves or that end
    // is reached. Driven by rAF because there is no CSS primitive for a continuous
    // scroll, and the per-frame delta is scaled by elapsed time so the speed is the
    // same on 60Hz and 120Hz displays.
    const scrollFrame = useRef( null );

    function stopScroll() {
        if ( scrollFrame.current !== null ) {
            cancelAnimationFrame( scrollFrame.current );
            scrollFrame.current = null;
        }
    }

    function startScroll( direction ) {
        const menu = holderRef.current?.querySelector( '.nav-menu' );
        if ( !menu ) { return; }

        stopScroll();

        let previous = null;

        function step( now ) {
            const limit = menu.scrollWidth - menu.clientWidth;
            const atEnd = direction < 0 ? menu.scrollLeft <= 0 : menu.scrollLeft >= limit;

            // stop at the edge so the loop is not left spinning against a hard stop
            if ( atEnd ) {
                stopScroll();
                return;
            }

            if ( previous !== null ) {
                menu.scrollLeft += direction * NAV_SCROLL_SPEED * ( now - previous ) / 1000;
            }

            previous = now;
            scrollFrame.current = requestAnimationFrame( step );
        }

        scrollFrame.current = requestAnimationFrame( step );
    }

    // covers the menu unmounting while the pointer is still over an arrow
    useEffect( () => stopScroll, [] );

    return (
        <>
            { companyLoading &&
                <Spin />
            }

            { !companyLoading &&
                <Layout>
                    <Header style={ { padding: 0 } }>

                        <div className='menu-holder' ref={ holderRef }>

                            <div className='overlay'>
                                <div
                                    className={ classNames( 'nav-overlay', { hidden: !overflow.left } ) }
                                    // style={ !overflow.left ? { width: 0, padding: 0 } : {} }
                                    onMouseEnter={ () => startScroll( -1 ) }
                                    onMouseLeave={ stopScroll }
                                >
                                    { /* { !!overflow.left && <LeftOutlined /> } */ }
                                    <LeftOutlined />
                                </div>
                                <div
                                    className={ classNames( 'nav-overlay', { hidden: !overflow.right } ) }
                                    // style={ !overflow.right ? { width: 0, padding: 0 } : {} }
                                    onMouseEnter={ () => startScroll( 1 ) }
                                    onMouseLeave={ stopScroll }
                                >
                                    { /* { !!overflow.right && <RightOutlined /> } */ }
                                    <RightOutlined />
                                </div>
                            </div>
                            <Menu
                                className="nav-menu"
                                selectedKeys={ selectedKeys }
                                mode="horizontal"
                                disabledOverflow
                                items={
                                    [
                                        {
                                            key: 'learning-content',
                                            label: <Link to={ Routes.LearningContentNav.buildPath() }>
                                                <div className='header-link'>
                                                    <AppstoreOutlined />
                                                    Learning Content
                                                    <CaretDownFilled />
                                                </div>
                                            </Link>,
                                            children: [
                                                {
                                                    key: 'learning-objects',
                                                    label: <Link to={ Routes.Objects.buildPath() }>
                                                        <div className='header-link'>
                                                            <BlockOutlined />
                                                            Learning Objects
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'courses',
                                                    label: <Link to={ Routes.Courses.buildPath() }>
                                                        <div className='header-link'>
                                                            <FormOutlined />
                                                            Courses
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'curriculums',
                                                    disabled: !showCurricula,
                                                    label: <Link to={ Routes.Curriculums.buildPath() }>
                                                        <div className='header-link'>
                                                            <SnippetsOutlined />
                                                            Curricula
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'resources',
                                                    disabled: !showResources,
                                                    label: <Link to={ Routes.ResourcesPage.buildPath() }>
                                                        <div className='header-link'>
                                                            <ReadOutlined />
                                                            Resources
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'ilt',
                                                    disabled: !showILT,
                                                    label: <Link to={ Routes.ILTPage.buildPath() }>
                                                        <div className='header-link'>
                                                            <UserOutlined />
                                                            Instructor Led Training
                                                        </div>
                                                    </Link>
                                                },
                                            ]
                                        },
                                        {
                                            key: 'content-taxonomy',
                                            label: <Link to={ Routes.ContentTaxonomyNav.buildPath() }>
                                                <div className='header-link'>
                                                    <PartitionOutlined />
                                                    Content Taxonomy
                                                    <CaretDownFilled />
                                                </div>
                                            </Link>,
                                            children: [
                                                {
                                                    key: 'categories',
                                                    disabled: !showCategories,
                                                    label: <Link to={ Routes.ListCategoriesView.buildPath() }>
                                                        <div className='header-link'>
                                                            <AppstoreAddOutlined />
                                                            Categories
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'tags',
                                                    label: <Link to={ Routes.TagsPage.buildPath() }>
                                                        <div className='header-link'>
                                                            <TagsOutlined />
                                                            Tags
                                                        </div>
                                                    </Link>
                                                },
                                            ]
                                        }, {
                                            key: 'gamification',
                                            label: <Link to={ Routes.GamificationNav.buildPath() }>
                                                <div className='header-link'>
                                                    <CrownOutlined />
                                                    Incentives
                                                    <CaretDownFilled />
                                                </div>
                                            </Link>,
                                            disabled: !( showObjectives || showLeaderboards || showBadges || showCertificates ),
                                            children: [
                                                {
                                                    key: 'objectives',
                                                    disabled: !showObjectives,
                                                    label: <Link to={ Routes.ObjectivesPage.buildPath() }>
                                                        <div className='header-link'>
                                                            <CarryOutOutlined />
                                                            Objectives
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'leaderboards',
                                                    disabled: !showLeaderboards,
                                                    label: <Link to={ Routes.ViewLeaderboardsPage.buildPath() }>
                                                        <div className='header-link'>
                                                            <TrophyOutlined />
                                                            Leaderboards
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'badges',
                                                    disabled: !showBadges,
                                                    label: <Link to={ Routes.ViewBadgesPage.buildPath() }>
                                                        <div className='header-link'>
                                                            <SafetyOutlined />
                                                            Badges
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'certificates',
                                                    disabled: !showCertificates,
                                                    label: <Link to={ Routes.ListCertificates.buildPath() }>
                                                        <div className='header-link'>
                                                            <BookOutlined />
                                                            Certificates
                                                        </div>
                                                    </Link>
                                                },
                                            ]
                                        },
                                        {
                                            key: 'notifications',
                                            disabled: !showNotifications,
                                            label: <Link to={ Routes.NotificationsPage.buildPath() }>
                                                <div className='header-link'>
                                                    <ClockCircleOutlined />
                                                    Notifications
                                                </div>
                                            </Link>
                                        },
                                        {
                                            key: 'pagesetup',
                                            disabled: !showPageSetup,
                                            label: <Link to={ Routes.ComponentsList.buildPath() }>
                                                <div className='header-link'>
                                                    <HomeOutlined />
                                                    { authService.isHoya()  ? 'My Journey Setup' : 'Homepage Setup' }
                                                </div>
                                            </Link>
                                        },
                                        {
                                            key: 'reporting',
                                            label: <Link to={ Routes.Reporting.buildPath() }>
                                                <div className='header-link'>
                                                    <BarChartOutlined />
                                                    Analytics
                                                    <CaretDownFilled />
                                                </div>
                                            </Link>,
                                            children: [
                                                {
                                                    key: 'data-export',
                                                    disabled: !showDataDump,
                                                    label: <Link to={ Routes.DataExportPage.buildPath() }>
                                                        <div className='header-link'>
                                                            <CloudServerOutlined />
                                                            Data Export
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'static-reporting',
                                                    disabled: !showStatichouseReporting,
                                                    label: <Link to={ Routes.StaticReporting.buildPath() }>
                                                        <div className='header-link'>
                                                            <DatabaseOutlined />
                                                            Static Reporting
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'ai-reporting',
                                                    disabled: !showAIReporting,
                                                    label: <Link to={ Routes.AIReport.buildPath() }>
                                                        <div className='header-link'>
                                                            <RobotOutlined />
                                                            AI Reporting
                                                        </div>
                                                    </Link>
                                                },
                                                {
                                                    key: 'stats',
                                                    label: <Link to={ Routes.Stats.buildPath() }>
                                                        <div className='header-link'>
                                                            <LineChartOutlined />
                                                            Stats
                                                        </div>
                                                    </Link>
                                                },
                                                ...( authService.isBiwEmployee() ? [ {
                                                    key: 'warehouse-imports',
                                                    label: <Link to={ Routes.WarehouseImports.buildPath() }>
                                                        <div className='header-link'>
                                                            <ImportOutlined />
                                                            Warehouse Imports
                                                        </div>
                                                    </Link>
                                                } ] : [] ),
                                            ]
                                        },
                                    ]
                                }
                            />
                        </div>
                    </Header>

                    <Content>
                        { props.children }
                    </Content>
                </Layout>
            }
        </>
    );
};

export default AppView;
